import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import useAuth from '../../auth/useAuth';

const { Title, Text } = Typography;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 400px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

function ChangePassword() {
  const { completePasswordChange, isAuthenticated, mustChangePassword } = useAuth();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async ({ currentPassword, newPassword }) => {
    setSubmitting(true);
    setError(null);
    try {
      await completePasswordChange(currentPassword, newPassword);
      // Navigates on success.
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // Reachable deliberately for a voluntary change; only redirect away once the
  // pending change is done and the form is idle.
  if (!mustChangePassword && !submitting) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page>
      <StyledCard>
        <Header>
          <Title level={3} style={{ marginBottom: 4 }}>
            Choose a new password
          </Title>
          <Text type="secondary">
            Your account was set up with a temporary password. Please replace it to continue.
          </Text>
        </Header>

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        <Form layout="vertical" onFinish={onFinish} requiredMark={false} disabled={submitting}>
          <Form.Item
            name="currentPassword"
            label="Temporary password"
            rules={[{ required: true, message: 'Please enter your temporary password.' }]}
          >
            <Input.Password autoComplete="current-password" size="large" autoFocus />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'Please enter a new password.' },
              { min: 8, message: 'Use at least 8 characters.' },
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" size="large" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your new password.' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match.'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" size="large" />
          </Form.Item>

          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            Set password
          </Button>
        </Form>
      </StyledCard>
    </Page>
  );
}

export default ChangePassword;
